import {ContactFormStore} from '@/Agent/ContactForm/ContactFormStore/ContactFormStore';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import InputText from '@/UI/EmcApplicationQuestions/inputs/core/InputText';
import {Button, FormControl, makeStyles, MenuItem, Select, Typography} from '@material-ui/core';
import {Grid} from '@material-ui/core';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {MaskedPattern} from 'imask';
import {observer} from 'mobx-react-lite';
import React from 'react';
import {useInstantApp} from './common/useInstantApp';
import {updateHubspotProsperDealBankingInfoProvidedUpfront, updateHubspotProsperDealConditionalCoverage} from '../deal';
import {useBankVerification} from '@/Agent/Common/useBankVerification';
import {BankVerificationError} from '@/Agent/Common/BankVerificationError';

interface ConditionalCoverageProps {
    parameterStore: ParameterStore;
    contactFormStore: ContactFormStore;
    ageniumContext: AgenuimContextType;
    onNext: () => void;
}

const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#3f51b5',
        marginBottom: '42px',
    },
    detailsWrap: {
        padding: '10px',
    },
    text: {
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    smallText: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#838383',
        marginTop: '18px',
    },
    list: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#838383',
        paddingLeft: '20px',
    },
    listItem: {
        display: 'list-item',
        listStyle: 'disc',
    },
    navigation: {
        marginTop: '20px',
    },
    backBtn: {
        borderRadius: '20px',
    },
    nextBtn: {
        minWidth: '400px',
        borderRadius: '20px',
    },
    progress: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: '10px',
        justifyContent: 'center',
    },
    button: {
        paddingTop: 16,
        paddingBottom: 16,
        fontSize: 26,
        lineHeight: '150%',
        textTransform: 'none',
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        height: 72,
        maxWidth: 288,
        color: '#174943',
        border: '1.75px solid',
        borderRadius: '8px',
        borderColor: '#456D69',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#F4F6FF',
        },
    },
    buttonActive: {
        backgroundColor: '#E8EBF8',
    },
    label: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
    },
    formControl: {
        minWidth: 240,
    },
}));

const ConditionalCoverage: React.FC<ConditionalCoverageProps> = observer(
    ({parameterStore, contactFormStore, ageniumContext, onNext}) => {
        const classes = useStyles();
        const {electConditionalCoverage} = useInstantApp();
        const [wantsConditionalCoverage, setWantsConditionalCoverage] = useState<boolean | null>(null);
        const {
            verificationResult,
            overrideConfirmed,
            setOverrideConfirmed,
            isVerifying,
            verifyBankAccount,
            resetVerification,
            canProceed,
        } = useBankVerification();

        const {onValueChangedPreApproval, approval} = parameterStore;

        const maskRoutingNumber = new MaskedPattern({mask: '{000000000}'});
        const maskAccountNumber = new MaskedPattern({mask: '{00000000000000000}'});

        const validateBankingInfo = useCallback(() => {
            return (
                approval.conditionalAccountNumber.value !== '' &&
                approval.conditionalRoutingNumber.value !== '' &&
                approval.conditionalAccountType.value !== ''
            );
        }, [
            approval.conditionalAccountNumber.value,
            approval.conditionalRoutingNumber.value,
            approval.conditionalAccountType.value,
        ]);

        const handleVerifyBankAccount = useCallback(async () => {
            if (!validateBankingInfo()) return {success: false};
            const accountType = approval.conditionalAccountType.value === 'CHECKING' ? 1 : 2;
            const result = await verifyBankAccount({
                routingNumber: approval.conditionalRoutingNumber.value!,
                accountNumber: approval.conditionalAccountNumber.value!,
                accountType,
                customerEmail: contactFormStore.email || parameterStore.contact || '',
                customerGuid: parameterStore.walkthroughData?.uuid || parameterStore.walkthroughData?.id,
                formId: parameterStore.formid,
                // ageniumCaseId not available
            });

            // If verification is successful and user wants conditional coverage, proceed automatically
            if (result && result.success && wantsConditionalCoverage === true) {
                // Perform the same actions as onNextClick for successful validation
                onValueChangedPreApproval(approval.conditionalCoverageElected, 'Yes');
                await electConditionalCoverage();
                await contactFormStore.saveForm();
                onNext();
            }

            return result;
        }, [
            approval.conditionalRoutingNumber.value,
            approval.conditionalAccountNumber.value,
            approval.conditionalAccountType.value,
            approval.conditionalCoverageElected,
            validateBankingInfo,
            verifyBankAccount,
            contactFormStore.email,
            contactFormStore.saveForm,
            parameterStore.contact,
            parameterStore.walkthroughData,
            parameterStore.formid,
            wantsConditionalCoverage,
            onValueChangedPreApproval,
            electConditionalCoverage,
            onNext,
        ]);

        useEffect(() => {
            ageniumContext.initSdk();
        }, []);

        const isConditionalCoverageNotElected = useMemo(
            () => approval.conditionalCoverageElected.value.length === 0,
            [],
        );

        useEffect(() => {
            async function setConditionalCoverageViewed() {
                // Set a non-empty value to note page was viewed
                if (!approval.conditionalCoverageElected.value) {
                    onValueChangedPreApproval(approval.conditionalCoverageElected, 'NA');
                    await contactFormStore.saveForm();
                }
            }
            setConditionalCoverageViewed();
        }, []);

        useEffect(() => {
            // Only autofill if conditional fields are empty
            if (!approval.conditionalAccountType.value && parameterStore.banking.accountType.value) {
                onValueChangedPreApproval(approval.conditionalAccountType, parameterStore.banking.accountType.value);
            }
            if (!approval.conditionalRoutingNumber.value && parameterStore.banking.routingNumber.value) {
                onValueChangedPreApproval(
                    approval.conditionalRoutingNumber,
                    parameterStore.banking.routingNumber.value,
                );
            }
            if (!approval.conditionalAccountNumber.value && parameterStore.banking.accountNumber.value) {
                onValueChangedPreApproval(
                    approval.conditionalAccountNumber,
                    parameterStore.banking.accountNumber.value,
                );
            }
        }, []);

        async function onSkipClick() {
            onValueChangedPreApproval(approval.conditionalCoverageElected, 'No');
            await updateHubspotProsperDealBankingInfoProvidedUpfront('No');
            await contactFormStore.saveForm();
            onNext();
        }

        async function onNextClick() {
            // If conditional coverage is selected, need to validate bank account
            if (wantsConditionalCoverage === true) {
                // Verify bank account if not already verified
                if (!verificationResult) {
                    const verifyResult = await handleVerifyBankAccount();
                    if (!verifyResult || !verifyResult.success) {
                        // Validation failed, don't proceed
                        return;
                    }
                }

                // Check if we can proceed with existing verification
                if (!canProceed()) {
                    return;
                }

                if (!validateBankingInfo()) {
                    return;
                }

                onValueChangedPreApproval(approval.conditionalCoverageElected, 'Yes');
                // Send the flag to the backend
                await electConditionalCoverage();
            } else {
                // Not opting for conditional coverage
                onValueChangedPreApproval(approval.conditionalCoverageElected, 'No');
                await updateHubspotProsperDealConditionalCoverage('No');
                await updateHubspotProsperDealBankingInfoProvidedUpfront('Yes');
            }

            await contactFormStore.saveForm();
            onNext();
        }

        return (
            <Grid container classes={{root: classes.wrapper}}>
                <Typography classes={{root: classes.title}} data-testId="conditionalCoverage">
                    Would Your Client Like To Opt-In To Conditional Coverage?
                </Typography>
                <Grid container classes={{root: classes.detailsWrap}} spacing={4}>
                    <Grid item xs={9}>
                        <Typography classes={{root: classes.text}}>
                            Immediate Coverage While The Application Is Being Reviewed
                        </Typography>
                        <Typography classes={{root: classes.smallText}}>
                            Here are the benefits of Conditional Coverage:
                        </Typography>
                        <ul style={{paddingLeft: '20px'}}>
                            <li className={classes.listItem}>
                                <Typography classes={{root: classes.list}}>
                                    Be protected while we review their application — at no extra cost.
                                </Typography>
                            </li>
                            <li className={classes.listItem}>
                                <Typography classes={{root: classes.list}}>
                                    They can add their bank account now for payment.
                                </Typography>
                            </li>
                            <li className={classes.listItem}>
                                <Typography classes={{root: classes.list}}>
                                    <span style={{color: '#3F51B5'}}>
                                        No charge will be made until their policy is approved.
                                    </span>
                                </Typography>
                            </li>
                        </ul>
                    </Grid>
                </Grid>
                <Grid container spacing={4} style={{marginTop: '30px', marginBottom: '40px'}}>
                    <Grid item style={{width: '288px'}}>
                        <Button
                            className={`${classes.button} ${wantsConditionalCoverage === true ? classes.buttonActive : ''}`}
                            variant="outlined"
                            fullWidth
                            onClick={() => setWantsConditionalCoverage(true)}
                        >
                            Yes
                        </Button>
                    </Grid>
                    <Grid item style={{width: '288px'}}>
                        <Button
                            className={`${classes.button} ${wantsConditionalCoverage === false ? classes.buttonActive : ''}`}
                            variant="outlined"
                            fullWidth
                            onClick={() => setWantsConditionalCoverage(false)}
                        >
                            No
                        </Button>
                    </Grid>
                </Grid>
                {wantsConditionalCoverage !== null && (
                    <Grid container spacing={2} direction="column">
                        {wantsConditionalCoverage === false && (
                            <Grid item style={{marginBottom: '30px'}}>
                                <Typography classes={{root: classes.text}}>
                                    Add your banking info now to speed things up later.
                                </Typography>
                            </Grid>
                        )}
                        <Grid item>
                            <Typography classes={{root: classes.text}}>
                                {wantsConditionalCoverage === true
                                    ? 'To lock in your coverage, we’ll just need your banking info.'
                                    : 'Banking Information'}
                            </Typography>
                        </Grid>
                        <Grid item container spacing={4}>
                            <Grid item xs={3}>
                                <Typography className={classes.label}>Bank Account Type</Typography>
                                <FormControl fullWidth variant="outlined" className={classes.formControl}>
                                    <Select
                                        aria-label="Bank Account Type"
                                        variant="outlined"
                                        value={approval.conditionalAccountType.value || ''}
                                        onChange={(e) => {
                                            onValueChangedPreApproval(
                                                approval.conditionalAccountType,
                                                e.target.value as string,
                                            );
                                            resetVerification();
                                            setOverrideConfirmed(false);
                                        }}
                                        placeholder="--"
                                    >
                                        {[
                                            ['CHECKING', 'Checking'],
                                            ['SAVINGS', 'Savings'],
                                        ].map(([value, label]) => (
                                            <MenuItem key={value} value={value}>
                                                {label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography className={classes.label}>Routing Number</Typography>
                                <InputText
                                    aria-label="Routing Number"
                                    mask={maskRoutingNumber}
                                    value={approval.conditionalRoutingNumber.value}
                                    onChange={(e) => {
                                        onValueChangedPreApproval(approval.conditionalRoutingNumber, e.target.value);
                                        resetVerification();
                                        setOverrideConfirmed(false);
                                    }}
                                    placeholder="--"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Typography className={classes.label}>Account Number</Typography>
                                <InputText
                                    aria-label="Account Number"
                                    mask={maskAccountNumber}
                                    value={approval.conditionalAccountNumber.value}
                                    onChange={(e) => {
                                        onValueChangedPreApproval(approval.conditionalAccountNumber, e.target.value);
                                        resetVerification();
                                        setOverrideConfirmed(false);
                                    }}
                                    placeholder="--"
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={12} style={{marginTop: '16px'}}>
                                <BankVerificationError
                                    verificationResult={verificationResult}
                                    overrideConfirmed={overrideConfirmed}
                                    onOverrideChange={setOverrideConfirmed}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={2} justify="space-around" className={classes.navigation}>
                    <Grid item>
                        <Button
                            color="primary"
                            variant="text"
                            onClick={onSkipClick}
                            className={classes.backBtn}
                            disabled={wantsConditionalCoverage === null || wantsConditionalCoverage === true}
                        >
                            Skip
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={onNextClick}
                            className={classes.nextBtn}
                            disabled={
                                wantsConditionalCoverage === null ||
                                !validateBankingInfo() ||
                                isVerifying ||
                                !!(wantsConditionalCoverage === true && verificationResult && !canProceed())
                            }
                        >
                            Next
                        </Button>
                    </Grid>
                </Grid>
                <code style={{color: '#00f', fontSize: '12px', marginTop: '100px', width: '100%'}}>
                    <div>
                        <b style={{color: '#f50'}}>Application Id:</b> {ageniumContext.applicationId}{' '}
                    </div>
                    <div>
                        <b style={{color: '#f50'}}>Application Number:</b> {ageniumContext.applicationNumber}
                    </div>
                    <div>
                        <b style={{color: '#f50'}}>Policy Number:</b> {ageniumContext.policyNumber}
                    </div>
                </code>
            </Grid>
        );
    },
);

export default ConditionalCoverage;
